import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import textUtils from '../common/utilities/TextUtils';
import {GENERIC_REQUEST_PARAMETER, ADD_UPDATE, getApiPath} from "../../api/ApiConst"
import apiCaller from "../../api/ApiCaller";
import { TYPES_OPTIONS } from "../../settings/constants/types";

// Helper function to find nested attributes
const findNestedAttributes = (attributes, path) => {
    if (!path.length) return attributes;
    const [current, ...rest] = path;
    const currentAttr = attributes[current];
    return rest.length ? findNestedAttributes(currentAttr.attributes, rest) : currentAttr.attributes;
};

// Helper function to get attribute path names
const getAttributePathNames = (attributes, path) => {
    if (!path.length) return [];
    const [current, ...rest] = path;
    const currentAttr = attributes[current];
    return [
        { index: current, name: currentAttr.name || `Attribute ${current + 1}` },
        ...getAttributePathNames(currentAttr.attributes, rest)
    ];
};

// Helper function to update nested attributes
const updateNestedAttributes = (attributes, path, newAttributes) => {
    if (!path.length) return newAttributes;
    const [current, ...rest] = path;
    const newArray = [...attributes];
    if (rest.length) {
        newArray[current] = {
            ...newArray[current],
            attributes: updateNestedAttributes(newArray[current].attributes, rest, newAttributes)
        };
    } else {
        newArray[current] = {
            ...newArray[current],
            attributes: newAttributes
        };
    }
    return newArray;
};

// Helper function to validate attribute name
const isValidAttributeName = (name) => {
    return name && name.trim().length > 0;
};

const BreadcrumbNav = ({ pathNames, onNavigate }) => {
    return (
        <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-3">
                <li className="breadcrumb-item">
                    <Button
                        variant="link"
                        className="p-0"
                        onClick={() => onNavigate([])}
                    >
                        Root
                    </Button>
                </li>
                {pathNames.map((item, index) => (
                    <li key={index} className="breadcrumb-item">
                        <Button
                            variant="link"
                            className="p-0"
                            onClick={() => onNavigate(pathNames.slice(0, index + 1).map(p => p.index))}
                        >
                            {item.name}
                        </Button>
                    </li>
                ))}
            </ol>
        </nav>
    );
};

const AttributeEditor = ({ attributes = [], onChange, currentPath = [], onPathChange }) => {
    const currentAttributes = findNestedAttributes(attributes || [], currentPath) || [];
    const pathNames = getAttributePathNames(attributes || [], currentPath);

    const handleAddAttribute = () => {
        const newAttributes = [...currentAttributes, { name: "", type: "", description: "", attributes: [] }];
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleRemoveAttribute = (index) => {
        const newAttributes = [...currentAttributes];
        newAttributes.splice(index, 1);
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleAttributeChange = (index, field, value) => {
        const newAttributes = [...currentAttributes];
        newAttributes[index] = { ...newAttributes[index], [field]: value };
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleNavigateToNested = (index) => {
        onPathChange([...currentPath, index]);
    };

    // More defensive check for canAddAttribute
    const canAddAttribute = Array.isArray(currentAttributes) && 
        (currentAttributes.length === 0 || currentAttributes.every(attr => attr && isValidAttributeName(attr.name)));

    return (
        <div>
            {currentPath.length > 0 && (
                <BreadcrumbNav
                    pathNames={pathNames}
                    onNavigate={onPathChange}
                />
            )}
            <div className="mb-3">
                {Array.isArray(currentAttributes) && currentAttributes.map((attr, index) => (
                    <div key={index} className="mb-3 p-2 border rounded">
                        <div className="row mb-2">
                            <div className="col-md-4">
                                <input
                                    type="text"
                                    className={`form-control ${!isValidAttributeName(attr?.name) ? 'is-invalid' : ''}`}
                                    placeholder="Attribute Name"
                                    value={attr?.name || ""}
                                    onChange={(e) => handleAttributeChange(index, "name", e.target.value)}
                                />
                                {!isValidAttributeName(attr?.name) && (
                                    <div className="invalid-feedback">
                                        Attribute name is required
                                    </div>
                                )}
                            </div>
                            <div className="col-md-4">
                                <Select
                                    options={TYPES_OPTIONS}
                                    value={{ label: attr?.type || "", value: attr?.type || "" }}
                                    onChange={(e) => handleAttributeChange(index, "type", e.value)}
                                    className="react-select-container"
                                />
                            </div>
                            <div className="col-md-2">
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleRemoveAttribute(index)}
                                >
                                    Remove
                                </Button>
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-md-8">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Description (optional)"
                                    value={attr?.description || ""}
                                    onChange={(e) => handleAttributeChange(index, "description", e.target.value)}
                                />
                            </div>
                        </div>
                        {(attr?.type === "OBJECT" || attr?.type === "ARRAYOFOBJ") && (
                            <div className="mt-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleNavigateToNested(index)}
                                    disabled={!isValidAttributeName(attr?.name)}
                                >
                                    Edit Nested Attributes ({attr?.attributes?.length || 0})
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
                <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={handleAddAttribute}
                    disabled={!canAddAttribute}
                >
                    Add Attribute
                </Button>
            </div>
        </div>
    );
};

const RequestParameterAddEdit = (props) => {
    const parameterTypeOptions = [
        { label: "Integer", value: "INT" },
        { label: "Double", value: "DOUBLE" },
        { label: "Long", value: "LONG" },
        { label: "Boolean", value: "BOOLEAN" },
        { label: "Date Time", value: "DATETIME" },
        { label: "Date", value: "DATE" },
        { label: "String", value: "STRING" },
        { label: "Array of Object", value: "ARRAYOFOBJ" },
        { label: "Size of Array", value: "SIZEOFARRAY" },
        { label: "UUId", value: "UUID" },
        { label: "Json List", value: "JSON_LIST" },
        { label: "Object", value: "OBJECT" },
        { label: "Collection Object", value: "COLLECTION_OBJECT" },
        { label: "Collection Object List", value: "COLLECTION_OBJECT_LIST" },
        { label: "ENUM", value: "ENUM" },
    ]
    const [parameterTypeKeyValue, setParameterTypeKeyValue] = useState("");
    const [currentPath, setCurrentPath] = useState([]);
    const [parameterObj, setParameterObj] = useState({
        id: "",
        name: "",
        description: "",
        type: "",
        requestId: "",
        required: false,
        attributes: [],
    });

    useEffect(() => {
        if (props.object != null) {
            setParameterObj({
                ...props.object,
                attributes: props.object.attributes || []
            });
            if (props.object.type !== "") {
                setParameterTypeKeyValue({
                    label: props.object.type,
                    value: props.object.type
                });
            } else {
                setParameterTypeKeyValue("");
            }
        }
        setCurrentPath([]);
    }, [props.object]);

    const toggle = () => {
        props.close();
    }

    //Text Change Event
    const handleTextChange = (e, type) => {
        if (type === "checkbox") {
            setParameterObj(prev => ({ ...prev, [e.target.id]: e.target.checked }));
        } else if (type === "text") {
            setParameterObj(prev => ({ ...prev, [e.target.id]: e.target.value }));
        } else if (type === "select") {
            setParameterTypeKeyValue(e);
            setParameterObj(prev => ({ ...prev, type: e.value }))
        }
    }

    const handleCreateParameter = () => {
        let requestObj = parameterObj;
        if ((textUtils.isEmpty(requestObj.name, "Name", true)) ||
            (textUtils.isEmpty(requestObj.description, "Description", true)) ||
            textUtils.isEmpty(requestObj.type, "Type", true)
        ) {
            return false;
        }

        // Validate attributes if type is OBJECT or ARRAYOFOBJ
        if ((parameterObj.type === "ARRAYOFOBJ" || parameterObj.type === "OBJECT") &&
            (!parameterObj.attributes || parameterObj.attributes.length === 0)) {
            toast.error("Please add at least one attribute for the object type");
            return false;
        }

        // Validate all attribute names and nested objects
        const validateAttributes = (attrs) => {
            if (!Array.isArray(attrs)) return true;
            
            return attrs.every(attr => {
                if (!attr) return true;

                // Check if attribute name is valid
                if (!isValidAttributeName(attr.name)) {
                    toast.error(`Attribute "${attr.name || 'Unnamed'}" must have a valid name`);
                    return false;
                }

                // Check if nested objects have at least one inner attribute
                if ((attr.type === "OBJECT" || attr.type === "ARRAYOFOBJ")) {
                    if (!attr.attributes || attr.attributes.length === 0) {
                        toast.error(`Nested object "${attr.name}" must have at least one inner attribute`);
                        return false;
                    }
                    // Recursively validate nested attributes
                    return validateAttributes(attr.attributes);
                }

                return true;
            });
        };

        // Always validate attributes if they exist, regardless of the main type
        if (parameterObj.attributes && parameterObj.attributes.length > 0) {
            if (!validateAttributes(parameterObj.attributes)) {
                return false;
            }
        }

        let url = getApiPath(GENERIC_REQUEST_PARAMETER, ADD_UPDATE);
        
        apiCaller.callPostApi(url, requestObj, {}, (response) => {
            toast.success(`Parameter ${parameterObj.id ? "Update" : "Created"} Successfully`);
            props.successCallBack();
            toggle();
        }, (error) => {
             toast.error(error.response.data.message);
        })
    }

    return (
        <>
            <Modal size="lg" show={props.show} onHide={toggle}>
                <Modal.Header closeButton>
                    <Modal.Title>{`${parameterObj?.id ? "Edit" : "Add"} Request Parameter`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <label>Name *</label>
                            <input type="text" className="form-control input-default"
                                id="name"
                                placeholder='Name'
                                onChange={(e) => handleTextChange(e, e.target.type)}
                                value={parameterObj.name}
                            />
                        </div>
                    </div>
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <label>Description *</label>
                            <input type="text" className="form-control input-default"
                                id="description"
                                placeholder='Description'
                                onChange={(e) => handleTextChange(e, e.target.type)}
                                value={parameterObj.description}
                            />
                        </div>
                    </div>
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <label>Type *</label>
                            <Select
                                options={parameterTypeOptions}
                                value={parameterTypeKeyValue}
                                id="type"
                                onChange={(e) => handleTextChange(e, "select")}
                                className="react-select-container"
                            />
                        </div>
                    </div>
                    {(parameterObj.type === "ARRAYOFOBJ" || parameterObj.type === "OBJECT") && (
                        <div className="form-group row mb-3">
                            <div className="col-lg-12">
                                <label>Attributes *</label>
                                <AttributeEditor
                                    attributes={parameterObj.attributes || []}
                                    onChange={(attributes) => setParameterObj(prev => ({ ...prev, attributes }))}
                                    currentPath={currentPath}
                                    onPathChange={setCurrentPath}
                                />
                            </div>
                        </div>
                    )}
                    <div className="form-group row mb-3">
                        <div className="col-lg-12">
                            <div className="row">

                                <div className="col-lg-6">
                                    <input
                                        type="checkbox"
                                        name="required"
                                        id="required"
                                        checked={parameterObj.required}
                                        onChange={(e) => handleTextChange(e, e.target.type)}
                                    />
                                    <label className='ms-2'>Required</label>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary"
                        onClick={handleCreateParameter}
                    >
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default RequestParameterAddEdit
